<div class="request-support">
    <div class="header-section" *ngIf="!magicLinkMode()">
        <div class="icon-circle healing">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 19c-1.8-1.4-3-3-3.2-4.8a3 3 0 0 1 2.7-3.2 2.8 2.8 0 0 1 2.1.8"/>
                <path d="M9.2 12c.7-.7 1.7-1 2.8-.8a3 3 0 0 1 2.7 3.2c-.2 1.8-1.4 3.4-3.2 4.8"/>
                <path d="M4.9 4.9c3.9-3.9 10.3-3.9 14.2 0 3.9 3.9 3.9 10.3 0 14.2"/>
                <path d="M19.1 4.9c3.9 3.9 3.9 10.3 0 14.2-3.9 3.9-10.3 3.9-14.2 0"/>
            </svg>
        </div>
        <h1>Share Your Journey</h1>
        <p class="subtitle">
            Sometimes the bravest thing we can do is let others show up for us.<br>
            By sharing your story, you give the world a chance to wrap its arms around you.
        </p>

        <!-- Progress bar -->
        <div class="progress-container">
            <div class="progress-info">
                <span>Step {{ currentStep() }} of {{ totalSteps }}</span>
            </div>
            <div class="progress-bar">
                <div class="progress-fill" [style.width.%]="progress()"></div>
            </div>
        </div>
    </div>

    <ng-container *ngIf="!magicLinkMode(); else magicLinkTemplate">

    <!-- Step 1: Tell Us Your Story -->
    <div class="step-content fade-in" *ngIf="currentStep() === 1">
        <div class="card">
            <h2>What are you or your loved one facing?</h2>

            <!-- Question 1: What are you or your loved one facing? -->
            <div class="form-group">
                <div class="options-grid situation-grid">
                    <button 
                        *ngFor="let situation of situationOptions"
                        type="button"
                        class="option-btn situation-btn"
                        [class.selected]="selectedSituation() === situation.id"
                        (click)="selectSituation(situation.id)">
                        {{ situation.label }}
                    </button>
                </div>
            </div>

            <!-- Question 2: Who is going through this? (shown after situation selected, except for grief) -->
            <div class="form-group separator" *ngIf="selectedSituation() && !isGriefSituation()">
                <label class="question-label">Who is going through this? <span class="required">*</span></label>
                <div class="options-grid support-recipient-grid">
                    <button 
                        *ngFor="let recipient of supportRecipientOptions"
                        type="button"
                        class="option-btn recipient-btn"
                        [class.selected]="whoNeedsSupport() === recipient.id"
                        (click)="selectWhoNeedsSupport(recipient.id)">
                        <span class="material-symbols-outlined">{{ recipient.icon }}</span>
                        <span>{{ recipient.label }}</span>
                    </button>
                </div>
            </div>

            <!-- Relationship question (shown for "A Loved One" or grief situations) -->
            <div class="form-group separator" *ngIf="(whoNeedsSupport() === 'someone-i-care-for' && !isGriefSituation()) || isGriefSituation()">
                <label class="question-label">
                    {{ isGriefSituation() ? 'What was your relationship to them?' : 'What is your relationship to them?' }}
                    <span class="required">*</span>
                </label>
                <div class="options-grid relationship-grid">
                    <button 
                        *ngFor="let relationship of caregiverRelationshipOptions"
                        type="button"
                        class="option-btn relationship-btn"
                        [class.selected]="caregiverRelationship() === relationship.id"
                        (click)="selectCaregiverRelationship(relationship.id)">
                        {{ relationship.label }}
                    </button>
                </div>
            </div>

            <div class="form-actions single-btn">
                <button type="button" class="btn-continue" [class.disabled]="!canContinueStep1()" (click)="nextStep()">
                    Continue <span class="material-symbols-outlined">arrow_forward</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Step 2: Where in the Journey -->
    <div class="step-content fade-in" *ngIf="currentStep() === 2">
        <div class="card">
            <h2>Where in the Journey?</h2>
            <p class="step-description">This helps us match your moment with the right kind of support.</p>

            <div class="form-group">
                <label class="question-label">Select the current moment <span class="required">*</span></label>
                <div class="options-grid journey-grid">
                    <button 
                        *ngFor="let stage of getJourneyStageOptions()"
                        type="button"
                        class="option-btn journey-btn"
                        [class.selected]="selectedJourneyStage() === stage.value"
                        (click)="selectJourneyStage(stage.value)">
                        {{ stage.label }}
                    </button>
                </div>
            </div>

            <div class="form-group" *ngIf="getContextTags().length > 0">
                <label class="question-label">Additional context (Optional)</label>
                <p class="help-text">Select any that apply to help us match better support.</p>
                <div class="tag-group">
                    <button 
                        *ngFor="let tag of getContextTags()"
                        type="button"
                        class="tag-btn"
                        [class.selected]="isContextTagSelected(tag)"
                        (click)="toggleContextTag(tag)">
                        {{ tag }}
                    </button>
                </div>
            </div>

            <div class="form-actions">
                <button type="button" class="btn-secondary" (click)="previousStep()">
                    Back
                </button>
                <button type="button" class="btn-continue" [class.disabled]="!selectedJourneyStage()" (click)="nextStep()">
                    Continue <span class="material-symbols-outlined">arrow_forward</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Step 3: What Brings Comfort -->
    <div class="step-content fade-in" *ngIf="currentStep() === 3">
        <div class="card">
            <h2>What Brings Comfort?</h2>
            <p class="step-description">Select the types of messages that would brighten your day.</p>

            <div class="form-group">
                <label class="question-label">Choose comfort zones <span class="required">*</span></label>
                <div class="comfort-zones-grid">
                    <button 
                        *ngFor="let zone of comfortZones"
                        type="button"
                        class="comfort-zone-btn"
                        [class.selected]="isZoneSelected(zone.id)"
                        (click)="toggleComfortZone(zone.id)">
                        <span class="material-symbols-outlined zone-icon">{{ zone.icon }}</span>
                        <span class="zone-label">{{ zone.label }}</span>
                    </button>
                </div>
                <div class="custom-zone-input" *ngIf="isZoneSelected('other')">
                    <input 
                        type="text" 
                        class="form-input"
                        placeholder="Tell us what brings you comfort..."
                        [value]="otherComfortText()"
                        (input)="otherComfortText.set($any($event.target).value)"
                        maxlength="100">
                    <span class="char-count">{{ otherComfortText().length }}/100</span>
                </div>
            </div>

            <div class="form-group">
                <label>Anything else to share? (Optional)</label>
                <textarea 
                    #additionalNoteInput
                    placeholder="Share anything else that might help us connect with the right messages..."
                    class="form-textarea"
                    rows="4"
                    [value]="step3Form.value.additionalNote || ''"
                    [attr.maxlength]="additionalNoteMaxLength"
                    (input)="step3Form.patchValue({ additionalNote: additionalNoteInput.value })"></textarea>
                <small class="help-text help-text--counter">
                    {{ (step3Form.value.additionalNote || '').length }}/{{ additionalNoteMaxLength }}
                </small>
            </div>

            <div class="form-actions">
                <button type="button" class="btn-secondary" (click)="previousStep()">
                    Back
                </button>
                <button type="button" class="btn-continue" [class.disabled]="selectedComfortZones().length === 0" (click)="nextStep()">
                    Continue <span class="material-symbols-outlined">arrow_forward</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Step 4: Just a Few Details -->
    <div class="step-content fade-in" *ngIf="currentStep() === 4">
        <div class="card">
            <h2>Just a Few Details</h2>
            <p class="step-description">Help supporters personalize their messages.</p>

            <form [formGroup]="step4Form">
                <div class="form-group">
                    <label>{{ getNameLabel() }} (Optional)</label>
                    <input 
                        type="text" 
                        formControlName="firstName"
                        placeholder="e.g., Sarah"
                        autocomplete="given-name"
                        class="form-input">
                    <div class="anonymous-option">
                        <input type="checkbox" id="postAnonymously" [checked]="isAnonymous()" (change)="toggleAnonymous()">
                        <label for="postAnonymously">Post anonymously</label>
                        <span class="info-icon" title="Your name won't be shown publicly">
                            <span class="material-symbols-outlined">info</span>
                        </span>
                    </div>
                </div>

                <div class="form-group">
                    <label>Email</label>
                    <input 
                        type="email" 
                        formControlName="email"
                        placeholder="your&#64;email.com"
                        autocomplete="email"
                        class="form-input">
                    <small class="help-text">If provided, we'll send a one-tap confirmation link. Never shown publicly.</small>
                </div>

                <div class="form-group">
                    <label>{{ getAgeLabel() }} <span class="required">*</span></label>
                    <input 
                        type="number" 
                        formControlName="age"
                        placeholder="e.g., 42"
                        class="form-input">
                </div>

                <div class="form-group">
                    <label>{{ getGenderLabel() }} (Optional)</label>
                    <div class="gender-buttons">
                        <button 
                            type="button"
                            class="gender-btn"
                            [class.selected]="step4Form.value.gender === 'male'"
                            (click)="step4Form.patchValue({ gender: 'male' })">
                            Male
                        </button>
                        <button 
                            type="button"
                            class="gender-btn"
                            [class.selected]="step4Form.value.gender === 'female'"
                            (click)="step4Form.patchValue({ gender: 'female' })">
                            Female
                        </button>
                    </div>
                </div>

                <div class="form-group">
                    <label>{{ getLocationLabel() }} (City, State) <span class="required">*</span></label>
                    <input 
                        #locationInput
                        type="text" 
                        formControlName="location"
                        placeholder="Start typing to search..."
                        class="form-input"
                        autocomplete="off">
                    <small class="help-text">Helps the community send regionally relevant support.</small>
                </div>

                <div class="form-group">
                    <label>Hospital or Care Facility (Optional)</label>
                    <input 
                        #hospitalInput
                        type="text" 
                        formControlName="hospital"
                        placeholder="Start typing to search..."
                        class="form-input"
                        autocomplete="off">
                    <small class="help-text">Where are you receiving care? This helps connect you with others nearby.</small>
                </div>
            </form>

            <div class="form-actions">
                <button type="button" class="btn-secondary" (click)="previousStep()">
                    Back
                </button>
                <button type="button" class="btn-continue" (click)="nextStep()" [disabled]="isSubmitting()">
                    <span *ngIf="!isSubmitting()">Submit Journey</span>
                    <span *ngIf="isSubmitting()" class="spinner"></span>
                    <span class="material-symbols-outlined" *ngIf="!isSubmitting()">arrow_forward</span>
                </button>
            </div>
        </div>
    </div>

    <!-- Step 5: Success -->
    <div class="step-content" *ngIf="currentStep() === 5">
        <div class="card success-card">
            <div class="success-icon">
                <span class="material-symbols-outlined">check_circle</span>
            </div>
            <h2>Journey Submitted for Review</h2>
            <p class="step-description">Your request will be reviewed before it appears on WondrVoices. Your unique Journey ID is:</p>

            <div class="email-display email-display--success" *ngIf="step4Form.value.email">
                <span class="material-symbols-outlined">mark_email_read</span>
                <span class="email-text">We sent a magic link to {{ step4Form.value.email }} so you can confirm your submission without typing a code.</span>
            </div>

            <div class="journey-id">
                <strong>{{ requestId() }}</strong>
                <p>Save this ID in case you need help checking on your submission later.</p>
            </div>

            <div class="what-happens">
                <h3>What happens next?</h3>
                <ul>
                    <li><span class="material-symbols-outlined">check</span> Our team will review your journey before publishing it</li>
                    <li><span class="material-symbols-outlined">check</span> If approved, supporters can send notes matched to your comfort zones</li>
                    <li><span class="material-symbols-outlined">check</span> If you shared an email, we'll notify you when new notes arrive</li>
                </ul>
            </div>

            <div class="form-actions">
                <button type="button" class="btn-continue" (click)="viewFeed()">
                    Browse Journeys
                </button>
                <button type="button" class="btn-secondary" (click)="backToHome()">
                    Back to Home
                </button>
            </div>
        </div>
    </div>

    </ng-container>

    <ng-template #magicLinkTemplate>
        <div class="step-content">
            <div class="card success-card confirmation-card">
                <div class="verification-icon" *ngIf="magicLinkStatus() === 'verifying'">
                    <span class="material-symbols-outlined">hourglass_top</span>
                </div>
                <div class="success-icon" *ngIf="magicLinkStatus() === 'success'">
                    <span class="material-symbols-outlined">verified</span>
                </div>
                <div class="verification-icon" *ngIf="magicLinkStatus() === 'error'">
                    <span class="material-symbols-outlined">warning</span>
                </div>

                <h2 *ngIf="magicLinkStatus() === 'verifying'">Confirming your submission...</h2>
                <h2 *ngIf="magicLinkStatus() === 'success'">Email Confirmed</h2>
                <h2 *ngIf="magicLinkStatus() === 'error'">Confirmation Link Expired</h2>

                <p class="step-description" *ngIf="magicLinkStatus() === 'verifying'">
                    Please wait while we verify your WondrVoices journey submission.
                </p>
                <p class="step-description" *ngIf="magicLinkStatus() === 'success'">
                    Your email is confirmed and your journey is now in review. Journey ID: <strong>{{ requestId() }}</strong>
                </p>
                <p class="step-description" *ngIf="magicLinkStatus() === 'error'">
                    {{ magicLinkError() || 'This confirmation link is no longer valid. Please submit again if needed.' }}
                </p>

                <div class="what-happens" *ngIf="magicLinkStatus() === 'success'">
                    <h3>What happens next?</h3>
                    <ul>
                        <li><span class="material-symbols-outlined">check</span> Our team will review your journey before publishing it</li>
                        <li><span class="material-symbols-outlined">check</span> If approved, supporters can send notes matched to your comfort zones</li>
                        <li><span class="material-symbols-outlined">check</span> We'll let you know when support arrives</li>
                    </ul>
                </div>

                <div class="form-actions">
                    <button type="button" class="btn-continue" (click)="viewFeed()">
                        Browse Journeys
                    </button>
                    <button type="button" class="btn-secondary" (click)="backToHome()">
                        Back to Home
                    </button>
                </div>
            </div>
        </div>
    </ng-template>
</div>
